import api from '@/services/utils/apiInstance.ts'

import type { PaginatedQuery } from '@/types/services/utils/request.type.ts'
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts'
import type {
  CreateSupportTicketRequest,
  SupportComment,
  SupportCommentResponse,
  SupportMessage,
  SupportMessageResponse,
  SupportTicketDetail,
  SupportTicketListItem,
  SupportTicketResponse,
  SupportUser,
  SupportUserResponse,
  TicketStatus,
  UpdateSupportTicketRequest,
} from '@/types/services/support/support.type.ts'

function mapUser(user: SupportUserResponse): SupportUser {
  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
  }
}

function mapMessage(message: SupportMessageResponse): SupportMessage {
  return {
    id: message.id,
    content: message.content,
    authorType: message.author_type,
    author: mapUser(message.author),
    createdAt: message.created_at,
  }
}

function mapComment(comment: SupportCommentResponse): SupportComment {
  return {
    id: comment.id,
    content: comment.content,
    author: mapUser(comment.author),
    createdAt: comment.created_at,
  }
}

function mapListItem(ticket: SupportTicketResponse): SupportTicketListItem {
  return {
    id: ticket.id,
    subject: ticket.subject,
    type: ticket.type,
    priority: ticket.priority,
    status: ticket.status,
    client: mapUser(ticket.client),
    assignee: ticket.assignee ? mapUser(ticket.assignee) : null,
    createdAt: ticket.created_at,
    updatedAt: ticket.updated_at,
  }
}

function mapDetail(ticket: SupportTicketResponse): SupportTicketDetail {
  return {
    ...mapListItem(ticket),
    createdBy: ticket.created_by ? mapUser(ticket.created_by) : mapUser(ticket.client),
    messages: (ticket.messages ?? []).map(mapMessage),
    comments: (ticket.comments ?? []).map(mapComment),
  }
}

class SupportService {
  async getPaginated(
    params: PaginatedQuery,
    status?: TicketStatus | null,
  ): Promise<PaginatedResponse<SupportTicketListItem>> {
    const query = new URLSearchParams()
    if (params.search) query.set('search', params.search)
    if (params.sort) query.set('sort', params.sort)
    query.set('order', params.order)
    query.set('limit', String(params.limit ?? 10))
    query.set('page', String(params.page ?? 1))
    if (status) query.set('status', status)

    const response = await api.get(`/support-ticket/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map(mapListItem),
    }
  }

  async get(ticketId: number): Promise<SupportTicketDetail> {
    const response = await api.get(`/support-ticket/${ticketId}`)
    return mapDetail(response.data)
  }

  async create(payload: CreateSupportTicketRequest): Promise<SupportTicketDetail> {
    const response = await api.post('/support-ticket', {
      client_id: payload.clientId,
      subject: payload.subject,
      content: payload.content,
      type: payload.type,
      priority: payload.priority,
      assignee_id: payload.assigneeId ?? null,
    })
    return mapDetail(response.data)
  }

  async update(ticketId: number, payload: UpdateSupportTicketRequest): Promise<SupportTicketDetail> {
    const body: Record<string, unknown> = {}
    if (payload.status !== undefined) body['status'] = payload.status
    if (payload.priority !== undefined) body['priority'] = payload.priority
    if ('assigneeId' in payload) body['assignee_id'] = payload.assigneeId ?? null

    const response = await api.patch(`/support-ticket/${ticketId}`, body)
    return mapDetail(response.data)
  }

  async addMessage(ticketId: number, content: string): Promise<SupportMessage> {
    const response = await api.post(`/support-ticket/${ticketId}/message`, { content })
    return mapMessage(response.data)
  }

  async addComment(ticketId: number, content: string): Promise<SupportComment> {
    const response = await api.post(`/support-ticket/${ticketId}/comment`, { content })
    return mapComment(response.data)
  }
}

export default new SupportService()
